import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Logo from "@/components/Logo";

export default function Footer() {
  const [email, setEmail] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Enter a valid email");
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the atelier");
      setEmail("");
    } catch {
      toast.error("Try again later");
    }
  };

  return (
    <footer className="border-t border-border bg-surface mt-32" data-testid="site-footer">
      {/* Newsletter band */}
      <div className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-20 grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          <div>
            <div className="ie-overline text-brand-accent mb-4">First Look</div>
            <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Quietly worn news,<br/>once a month.
            </h3>
          </div>
          <form onSubmit={submit} className="flex items-end gap-4 border-b border-foreground pb-2" data-testid="footer-newsletter-form">
            <input
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-transparent border-0 outline-none py-3 placeholder:text-text-secondary"
              data-testid="footer-newsletter-input"
              required
            />
            <button type="submit" className="ie-overline hover:text-brand-accent" data-testid="footer-newsletter-submit">Subscribe →</button>
          </form>
        </div>
      </div>

      {/* Columns */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 sm:py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
        {/* Brand block — full width on mobile, 4 cols on desktop */}
        <div className="md:col-span-4 pb-10 md:pb-0 border-b md:border-b-0 border-border">
          <Logo size="md" />
          <p className="text-sm text-text-secondary mt-5 md:mt-6 leading-relaxed max-w-sm">
            INDUS EIRENE is a quiet luxury house — where Indian heritage meets the poise of Greek serenity. Every piece is hand-finished, made to outlast trends.
          </p>
          <div className="mt-6 md:mt-8 space-y-3 text-sm text-text-secondary">
            <div className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0"/> Atelier · Madhya Pradesh · India</div>
            <div className="flex items-start gap-3"><Mail className="w-4 h-4 mt-0.5 flex-shrink-0"/> care@induseirene.in</div>
            <div className="flex items-start gap-3"><Instagram className="w-4 h-4 mt-0.5 flex-shrink-0"/> @induseirene</div>
          </div>
        </div>

        {/* Link columns — 2x2 on mobile, 4 cols on desktop */}
        <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
          <FooterCol title="Shop" links={[["Women", "/shop/women"], ["Men", "/shop/men"], ["New In", "/shop?sort=newest"], ["Bestsellers", "/shop"]]} />
          <FooterCol title="House" links={[["The Atelier", "/atelier"], ["Sustainability", "/sustainability"], ["Stories", "/stories"]]} />
          <FooterCol title="Care" links={[["Track Order", "/track"], ["Shipping & Delivery", "/shipping"], ["Returns & Exchanges", "/returns"], ["Size Guide", "/size-guide"], ["Contact", "/contact"]]} />
          <FooterCol title="Legal" links={[["Privacy", "/privacy"], ["Terms", "/terms"]]} />
        </div>
      </div>

      <div className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-5 md:py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="ie-overline text-text-secondary text-center md:text-left">© {new Date().getFullYear()} INDUS EIRENE · Crafted in India · Worn Worldwide</div>
          <div className="ie-overline text-text-secondary">Visa · Mastercard · UPI · Razorpay</div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="ie-overline text-foreground mb-4 md:mb-5">{title}</div>
      <ul className="space-y-2.5 md:space-y-3">
        {links.map(([label, href]) => (
          <li key={label}>
            <Link to={href} className="text-sm text-text-secondary hover:text-foreground transition-colors">{label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
